// src/gestion-logistics/components/AccountMasterManager.tsx
import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Popconfirm,
  Tooltip,
  Select,
  Input,
  Row,
  Col,
  Typography,
  Switch,
  Statistic,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DollarOutlined,
  FileTextOutlined,
  SwapOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import type { ColumnType } from "antd/es/table";
import { useAccountMasters } from "../hooks/useAccountMasters";
import { useLogisticsPermissions } from "../hooks/useLogisticsPermissions";
import { AccountMaster, AccountTypeCode } from "../types/costing.types";
import AccountMasterFormModal from "./AccountMasterFormModal";
import { useSelection } from "../../SelectionContext";

const { Title, Text } = Typography;
const { Option } = Select;

/**
 * Gestionnaire des rubriques comptables (Account Masters)
 * Permet de configurer les comptes INCOME, EXPENSE, et DISBURSEMENT
 */
const AccountMasterManager: React.FC = () => {
  const {
    accountMasters,
    accountTypes,
    loading,
    fetchAccountMasters,
    fetchAccountTypes,
    deleteAccountMaster,
    toggleAccountMasterStatus,
  } = useAccountMasters();

  const permissions = useLogisticsPermissions();
  const { selectedCompany } = useSelection();

  const [searchText, setSearchText] = useState("");
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<string | undefined>(
    undefined
  );
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [selectedAccountMaster, setSelectedAccountMaster] =
    useState<AccountMaster | null>(null);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");

  // Charger les rubriques comptables uniquement si une entreprise est sélectionnée
  useEffect(() => {
    if (selectedCompany) {
      fetchAccountMasters();
      fetchAccountTypes();
    }
  }, [selectedCompany]);

  const handleCreate = () => {
    setSelectedAccountMaster(null);
    setFormMode("create");
    setFormModalVisible(true);
  };

  const handleEdit = (record: AccountMaster) => {
    setSelectedAccountMaster(record);
    setFormMode("edit");
    setFormModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    await deleteAccountMaster(id);
    // Auto-refresh après suppression
    if (selectedCompany) {
      fetchAccountMasters();
    }
  };

  const handleToggleStatus = async (id: number) => {
    await toggleAccountMasterStatus(id);
    // Auto-refresh après modification statut
    if (selectedCompany) {
      fetchAccountMasters();
    }
  };

  const handleCloseModal = () => {
    setFormModalVisible(false);
    setSelectedAccountMaster(null);
    // Auto-refresh après création/modification
    if (selectedCompany) {
      fetchAccountMasters();
    }
  };

  // Filtrer les account masters
  const filteredData = accountMasters.filter((am) => {
    const matchSearch =
      searchText === "" ||
      am.code.toLowerCase().includes(searchText.toLowerCase()) ||
      am.name.toLowerCase().includes(searchText.toLowerCase()) ||
      (am.description &&
        am.description.toLowerCase().includes(searchText.toLowerCase()));

    const matchType =
      !selectedTypeFilter || am.account_type_code === selectedTypeFilter;

    return matchSearch && matchType;
  });

  // Statistiques
  const stats = {
    total: accountMasters.length,
    active: accountMasters.filter((am) => am.is_active).length,
    income: accountMasters.filter((am) => am.account_type_code === AccountTypeCode.INCOME)
      .length,
    expense: accountMasters.filter(
      (am) => am.account_type_code === AccountTypeCode.EXPENSE
    ).length,
    disbursement: accountMasters.filter(
      (am) => am.account_type_code === AccountTypeCode.DISBURSEMENT
    ).length,
  };

  const getAccountTypeIcon = (code: AccountTypeCode) => {
    switch (code) {
      case AccountTypeCode.INCOME:
        return <DollarOutlined />;
      case AccountTypeCode.EXPENSE:
        return <FileTextOutlined />;
      case AccountTypeCode.DISBURSEMENT:
        return <SwapOutlined />;
      default:
        return null;
    }
  };

  const getAccountTypeColor = (code: AccountTypeCode) => {
    switch (code) {
      case AccountTypeCode.INCOME:
        return "green";
      case AccountTypeCode.EXPENSE:
        return "orange";
      case AccountTypeCode.DISBURSEMENT:
        return "blue";
      default:
        return "default";
    }
  };

  const columns: ColumnType<AccountMaster>[] = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      width: 120,
      fixed: "left",
      render: (text: string) => (
        <Text strong style={{ color: "#1890ff" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Nom de la Rubrique",
      dataIndex: "name",
      key: "name",
      width: 250,
      ellipsis: true,
    },
    {
      title: "Type de Compte",
      dataIndex: "account_type_code",
      key: "account_type_code",
      width: 180,
      align: "center",
      render: (code: AccountTypeCode, record) => (
        <Tag
          icon={getAccountTypeIcon(code)}
          color={getAccountTypeColor(code)}
        >
          {record.account_type_name}
        </Tag>
      ),
    },
    {
      title: "Unité par Défaut",
      dataIndex: "default_unit",
      key: "default_unit",
      width: 120,
      align: "center",
      render: (unit: string) => unit || <Text type="secondary">-</Text>,
    },
    {
      title: "Prix Unitaire",
      dataIndex: "default_unit_price",
      key: "default_unit_price",
      width: 130,
      align: "right",
      render: (price: number | string) => {
        const numericPrice = typeof price === "string" ? parseFloat(price) : price;
        return numericPrice ? (
          <Text strong>{numericPrice.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</Text>
        ) : (
          <Text type="secondary">-</Text>
        );
      },
    },
    {
      title: "Taxe",
      dataIndex: "tax_applicable",
      key: "tax_applicable",
      width: 80,
      align: "center",
      render: (applicable: boolean) =>
        applicable ? (
          <Tag color="blue">Oui</Tag>
        ) : (
          <Tag color="default">Non</Tag>
        ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 200,
      ellipsis: true,
      render: (text: string) => text || <Text type="secondary">-</Text>,
    },
    {
      title: "Statut",
      dataIndex: "is_active",
      key: "is_active",
      width: 100,
      align: "center",
      render: (isActive: boolean, record) =>
        permissions.canEditAccountMasters ? (
          <Switch
            checked={isActive}
            onChange={() => handleToggleStatus(record.id)}
            checkedChildren="Actif"
            unCheckedChildren="Inactif"
            size="small"
          />
        ) : isActive ? (
          <Tag color="success">Actif</Tag>
        ) : (
          <Tag color="default">Inactif</Tag>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: "right",
      align: "center",
      render: (_, record) => (
        <Space size="small">
          {permissions.canEditAccountMasters && (
            <Tooltip title="Modifier">
              <Button
                type="text"
                icon={<EditOutlined />}
                size="small"
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
          )}

          {permissions.canDeleteAccountMasters && (
            <Popconfirm
              title="Supprimer cette rubrique ?"
              description="Cette action est irréversible. Les lignes de job costing utilisant cette rubrique ne seront pas affectées."
              onConfirm={() => handleDelete(record.id)}
              okText="Supprimer"
              cancelText="Annuler"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Supprimer">
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={
          <Title level={4} style={{ margin: 0 }}>
            Gestion des Rubriques Comptables
          </Title>
        }
        extra={
          permissions.canCreateAccountMasters && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Nouvelle Rubrique
            </Button>
          )
        }
      >
        {/* Statistiques */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={8} md={4}>
            <Statistic title="Total" value={stats.total} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Statistic
              title="Actives"
              value={stats.active}
              valueStyle={{ color: "#52c41a" }}
            />
          </Col>
          <Col xs={12} sm={8} md={5}>
            <Statistic
              title="Revenue (INCOME)"
              value={stats.income}
              prefix={<DollarOutlined />}
              valueStyle={{ color: "#389e0d" }}
            />
          </Col>
          <Col xs={12} sm={8} md={5}>
            <Statistic
              title="Expense"
              value={stats.expense}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: "#d46b08" }}
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Statistic
              title="Disbursement"
              value={stats.disbursement}
              prefix={<SwapOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Col>
        </Row>

        {/* Filtres */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Rechercher par code, nom ou description..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder="Filtrer par type de compte"
              value={selectedTypeFilter}
              onChange={setSelectedTypeFilter}
              allowClear
              style={{ width: "100%" }}
            >
              {accountTypes.map((type) => (
                <Option key={type.code} value={type.code}>
                  {type.name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={{
            pageSize: 20,
            showSizeChanger: false,
            showTotal: (total) => `Total: ${total} rubrique(s)`,
          }}
          size="small"
        />

        {/* Modal Formulaire */}
        <AccountMasterFormModal
          visible={formModalVisible}
          onClose={handleCloseModal}
          accountMaster={selectedAccountMaster}
          mode={formMode}
        />
      </Card>
    </div>
  );
};

export default AccountMasterManager;
